import React, { useCallback, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import { useLocation, useRouteMatch } from 'react-router-dom';
import { useNavigate } from 'react-router-dom-v5-compat';

import { LoadingIndicator } from 'ui-components/components/loading-indicator';
import { NotificationService, NOTIFICATION_TYPE_ERROR, NOTIFICATION_DURATIONS } from 'ui-components/services/notification-service';
import { Text } from 'ui-components/components/text';
import { TextType } from 'ui-components/components/text/text-type';
import { FlexColumn, Gap, FlexRow } from 'ui-components/components/flex';
import { ResponsiveWidth } from 'ui-components/components/responsive-layout';

import Global from '../../../../store/global-selectors';
import { SubscriptionPageWrapper } from '../../subscription-page-wrapper';
import { pages } from '../../nav-items';
import { getArchivePath, getBalanceHistoryPathForWalletId, getPostageTransactionsPath } from '../../../../routes/constants';
import { useWalletSetting } from '../../../../wallet/contexts/wallet-data-context-provider';
import { useWalletEligibility } from '../../../../wallet/hooks/use-wallet-eligibility';
import { walletSettingsEnum } from '../../../../enums/wallet-settings';
import { useDeriveSubscriptionInfo } from '../hooks/use-derive-subscription-info';
import { useEnableBalanceHistory } from '../../../../feature-flags/hooks/use-enable-balance-history';
import { useEnableAddOnCentral } from '../../../../feature-flags/hooks/use-enable-add-on-central';
import { useBuildMarketplaceAddOns } from '../../../../hooks/add-ons/use-build-addons';
import { reactivateSubscription } from '../../api/subscription';
import { refreshSellerPlanAndSubscriptionInfo, updateSellerPlanInfo } from '../../../../store/seller/seller.actions';
import { PaymentsTableAuctanePay } from '../components/payments-table/payments-table-auctane-pay';
import CardContainer from '../cards/card-container';
import SubscriptionCard from '../cards/subscription';
import { EmptyAddOnsCard } from '../cards/add-ons/empty-state';
import { AddOnsCardWrapper } from '../cards/add-ons';
import { WalletBalanceCard } from '../cards/wallet-balance';
import { LinkCard } from '../cards/link-card/index';
import { BRIGHTBACK_CANCELLATION_QUERY_STRING } from '../sections/cancellation/brightback/hooks/use-get-brightback-data';
import { usePaymentAndSubscriptionPageContext } from '../context/payment-and-subscription-page-context';
import { useGlobalModalLaunchContext } from '../../../../contexts/modal-context/global-modal-context';
import { PageEvents, PageEventSourceStrings, useTrackEvent } from '../hooks/use-track-event';
import BannerSection from '../sections/banner';
import { TRIAL_EXPIRED, CANCELLED } from '../constants/account-issues';
import { TaxIdentifierPanel } from '../components/tax-identifier-panel/tax-identifier-panel';
import { PendingSubscriptionChangeBanner } from '../pending-subscription-change-banner';
import { useTaxIdConfig } from '../hooks/use-tax-id-config';
import { MigratePlanBanner } from '../components/migrate-plan-modal/migration-banner';

import { LogService } from '../../../../services/log-service';
import messages from './messages';
import style from './style.module.less';

const paymentsAndSubscriptionRoute = `/${pages.settings.location}/${pages.paymentAndSubscription.location}`;

const shouldOptimisticallyShowCancellationBlock = (searchParams) => {
  return searchParams === BRIGHTBACK_CANCELLATION_QUERY_STRING;
};

const hasWalletToRegister = (eligibleWallet) => {
  return eligibleWallet && !eligibleWallet.isSetupComplete && !!eligibleWallet.newLabelProviderIds?.length;
};
export const PaymentAndSubscriptionPageContent = () => {
  // we iframe this page into v2. We disable a bunch of stuff and show a banner for people to go to the new version
  const isForV2 = useRouteMatch('/v2Subscriptions');
  const dispatch = useDispatch();
  const pageRef = React.createRef();
  const planInfo = useSelector(Global.selectSellerPlanInfo);
  const { isPlanLocked } = planInfo;
  const enableAddOnCentral = useEnableAddOnCentral();
  const wallets = useSelector(Global.selectSellerWallets);
  const { walletId } = wallets[0] ?? {};
  const { canUseSetting: allowBalanceHistory } = useWalletSetting(walletId, walletSettingsEnum.balanceHistory);
  const enableBalanceHistory = useEnableBalanceHistory();
  const { search: searchParams } = useLocation();
  const didSellerCancelViaBrightback = !!searchParams && shouldOptimisticallyShowCancellationBlock(searchParams);
  const navigate = useNavigate();
  const { accountIssueStatus } = useDeriveSubscriptionInfo(planInfo);
  const shouldDisableWalletInteractions = [TRIAL_EXPIRED, CANCELLED].includes(accountIssueStatus);
  const { eligibleWallet } = useWalletEligibility();
  const { isTaxIdAvailable } = useTaxIdConfig();

  const registeredWallets = wallets.filter((w) => w.isSetupComplete && w.walletId !== eligibleWallet?.walletId);
  const isWalletEligibleCountry = registeredWallets.length || hasWalletToRegister(eligibleWallet);

  const clearSearchParams = useCallback(() => {
    navigate(paymentsAndSubscriptionRoute, { replace: true });
  }, [navigate]);

  const reactivatePlan = useCallback(() => {
    reactivateSubscription()
      .then((resp) => {
        if (resp?.data?.planInfo) {
          dispatch(updateSellerPlanInfo(resp.data.planInfo));
        } else {
          dispatch(refreshSellerPlanAndSubscriptionInfo());
        }
      })
      .catch((err) => {
        LogService.logError(new Error('Error reactivating subscription', { cause: err }));
        dispatch(refreshSellerPlanAndSubscriptionInfo());
        NotificationService.showNotification({
          notificationType: NOTIFICATION_TYPE_ERROR,
          title: <FormattedMessage {...messages.undoCancellationErrorTitle} />,
          content: <FormattedMessage {...messages.undoCancellationErrorBody} />,
          duration: NOTIFICATION_DURATIONS.MEDIUM,
        });
      });

    if (didSellerCancelViaBrightback) {
      clearSearchParams();
    }
  }, [clearSearchParams, didSellerCancelViaBrightback, dispatch]);

  const { enabledAddons } = useBuildMarketplaceAddOns();
  const shouldShowEmptyAddOnsCard = enableAddOnCentral && !enabledAddons.length;
  const shouldShowAddOnsCard = enableAddOnCentral && !!enabledAddons.length;
  const { openManageSubscriptionDrawer } = useGlobalModalLaunchContext();
  const trackSubscriptionChangeStart = useTrackEvent(PageEvents.SUBSCRIPTION_CHANGE_START, PageEventSourceStrings.SHIPPY_ASSISTANT);

  const { isPlanInfoLoading, isSubscriptionDataLoading } = usePaymentAndSubscriptionPageContext();

  useEffect(() => {
    if (isPlanInfoLoading || isSubscriptionDataLoading) {
      return;
    }

    const params = new URLSearchParams(searchParams);
    if (params.get('openManageSubscription') !== 'true' || params.get('source') !== 'shippy') {
      return;
    }

    navigate(paymentsAndSubscriptionRoute, { replace: true });
    trackSubscriptionChangeStart();
    openManageSubscriptionDrawer();
  }, [isPlanInfoLoading, isSubscriptionDataLoading, navigate, openManageSubscriptionDrawer, searchParams, trackSubscriptionChangeStart]);

  if (isPlanInfoLoading || isSubscriptionDataLoading) {
    return <LoadingIndicator />;
  }

  return (
    <SubscriptionPageWrapper ref={pageRef} reactivatePlan={reactivatePlan}>
      <BannerSection accountIssueStatus={accountIssueStatus} reactivatePlan={reactivatePlan} />
      <MigratePlanBanner className={style['migration-banner']} />
      <FlexColumn gap={Gap.Space16} responsiveWidth={ResponsiveWidth.Cols12}>
        <Text className={style['section-title']} textType={TextType.H2} value={messages.payment} />
        <FlexRow gap={Gap.Dynamic} responsiveWidth={ResponsiveWidth.Cols12}>
          {isWalletEligibleCountry && (
            <CardContainer
              className={style['subscription-card-container']}
              disabledTooltipMessage={<FormattedMessage {...messages.activeSubscriptionNeeded} />}
              shouldDisableContents={shouldDisableWalletInteractions}
            >
              <WalletBalanceCard walletId={walletId} />
            </CardContainer>
          )}
          <CardContainer isFullWidth={!isWalletEligibleCountry} isPaymentCard>
            <PaymentsTableAuctanePay />
          </CardContainer>
        </FlexRow>
      </FlexColumn>

      <FlexColumn gap={Gap.Space16} responsiveWidth={ResponsiveWidth.Cols12}>
        <Text className={style['section-title']} textType={TextType.H2} value={messages.subscription} />
        <PendingSubscriptionChangeBanner className={style['pending-subscription-banner']} />
        <FlexRow gap={Gap.Dynamic} responsiveWidth={ResponsiveWidth.Cols12}>
          <CardContainer className={style['subscription-card-container']}>
            <SubscriptionCard didSellerCancelViaBrightback={didSellerCancelViaBrightback} reactivatePlan={reactivatePlan} />
          </CardContainer>
          <FlexColumn gap={Gap.Space24} responsiveWidth={ResponsiveWidth.Cols6}>
            {shouldShowAddOnsCard && !isForV2 && (
              <CardContainer className={style['subscription-card-container']}>
                <AddOnsCardWrapper />
              </CardContainer>
            )}
            {shouldShowEmptyAddOnsCard && !isForV2 && (
              <CardContainer className={style['subscription-card-container']}>
                <EmptyAddOnsCard />
              </CardContainer>
            )}
            {isTaxIdAvailable && (
              <CardContainer className={style['subscription-card-container']}>
                <TaxIdentifierPanel />
              </CardContainer>
            )}
            {!isForV2 && (
              <FlexRow className={style['card-collection']} gap={Gap.Dynamic} responsiveWidth={ResponsiveWidth.Cols6}>
                {allowBalanceHistory && enableBalanceHistory && (
                  <CardContainer isClickable>
                    <LinkCard
                      description={<FormattedMessage {...messages.transactionHistoryInfo} />}
                      link={getBalanceHistoryPathForWalletId(walletId)}
                      title={<FormattedMessage {...messages.transactionHistory} />}
                    />
                  </CardContainer>
                )}
                <CardContainer isClickable>
                  <LinkCard
                    description={<FormattedMessage {...messages.reportingInfo} />}
                    link={getPostageTransactionsPath()}
                    title={<FormattedMessage {...messages.reportingTitle} />}
                  />
                </CardContainer>
                {!isPlanLocked && (
                  <CardContainer isClickable>
                    <LinkCard
                      description={<FormattedMessage {...messages.dataArchiveInfo} />}
                      link={getArchivePath()}
                      title={<FormattedMessage {...messages.dataArchiveTitle} />}
                    />
                  </CardContainer>
                )}
              </FlexRow>
            )}
          </FlexColumn>
        </FlexRow>
      </FlexColumn>
    </SubscriptionPageWrapper>
  );
};

export default PaymentAndSubscriptionPageContent;
